import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import DomHelper from "../../../shared/helper/DomHelper";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import Administration from "../../session/Administration";
import {
	ConfigurationType,
	User,
	UserInterface,
	Users
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import UserDialogFields from "../field/UserDialogFields";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {InputText} from "primereact/inputtext";
import {Password} from "primereact/password";
import {Dropdown} from "primereact/dropdown";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import Info, {InfoType} from "../../../shared/components/Info";
import FunctionHelper from "../../../shared/helper/FunctionHelper";

export interface UserDialogStatus extends AdminDialogState<Users> {
	userFieldErrors: Map<UserDialogFields, Map<string, string>>
}

export class UserDialogDefinition extends AdminDialog<Users,
	AdminDialogProperties<Users>, UserDialogStatus> {
	constructor(props: Readonly<AdminDialogProperties<Users>>) {
		super(props);

		this.state = {
			...this.state,
			userFieldErrors: new Map<UserDialogFields, Map<string, string>>()
		};
	}

	public renderContents(): ReactNode {
		let parameter: Users | undefined = this.state.parameter;
		let errors: Map<UserDialogFields, Map<string, string>> = this.state.userFieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let admins: number = 0;
		for (let user of parameter!.user!.values()) {
			if (user.roles!.indexOf("admin") > -1) {
				admins++;
			}
		}

		return (
			<fieldset className="p-col-12 p-grid">
				<div className="p-col-12">
					<DataTable value={parameter!.user} responsiveLayout={"scroll"}>
						<Column field="username" header={
							<>
								<Translate {...this.props} data="user.username" namespace="dialog"/>
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={User.getUsernameDescription()}/>
							</>
						} body={(item: User & { index: number }) => {
							return <>
								<InputText value={FunctionHelper.applyValue(item.username, User.getUsernameDefault)}
										   className={
											   errors.has(UserDialogFields.USERNAME) &&
											   errors.get(UserDialogFields.USERNAME)!.has(item.uniqueId!) ?
												   "p-invalid" : ""
										   }
										   tooltip={
											   errors.has(UserDialogFields.USERNAME) &&
											   errors.get(UserDialogFields.USERNAME)!.has(item.uniqueId!) ?
												   Translate.value(
													   errors.get(UserDialogFields.USERNAME)!.get(item.uniqueId!)!,
													   {ns: "message"}
												   ) : ""
										   }
										   onInput={(e) => this.onChange(
											   UserDialogFields.USERNAME, {
												   uniqueId: item.uniqueId, value: e.currentTarget.value
											   }
										   )}/>
								{this.renderFieldError(errors.has(UserDialogFields.USERNAME) &&
									errors.get(UserDialogFields.USERNAME)!.has(item.uniqueId!) ?
										Translate.value(
											errors.get(UserDialogFields.USERNAME)!.get(item.uniqueId!)!,
											{ns: "message"}
										) : ""
								)}
							</>
						}}/>
						<Column field="password" header={
							<>
								<Translate {...this.props} data="user.password.label" namespace="dialog"/>
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={User.getPasswordDescription()}/>
							</>
						} body={(item: User & { index: number }) => {
							return <>
								<Password feedback={false} toggleMask={true}
										  value={FunctionHelper.applyValue(item.password, User.getPasswordDefault())}
										  appendTo={document.getElementById("root")}
										  className={
											  errors.has(UserDialogFields.PASSWORD) &&
											  errors.get(UserDialogFields.PASSWORD)!.has(item.uniqueId!) ?
												  "p-invalid" : ""
										  }
										  tooltip={
											  errors.has(UserDialogFields.PASSWORD) &&
											  errors.get(UserDialogFields.PASSWORD)!.has(item.uniqueId!) ?
												  Translate.value(
													  errors.get(UserDialogFields.PASSWORD)!.get(item.uniqueId!)!,
													  {ns: "message"}
												  ) : ""
										  }
										  placeholder={Translate.value(
											  "user.password.placeholder", {ns: "dialog"}
										  )}
										  onChange={(e) => this.onChange(UserDialogFields.PASSWORD, {
											  uniqueId: item.uniqueId, value: e.currentTarget.value
										  })}/>
								{this.renderFieldError(errors.has(UserDialogFields.PASSWORD) &&
									errors.get(UserDialogFields.PASSWORD)!.has(item.uniqueId!) ?
										Translate.value(
											errors.get(UserDialogFields.PASSWORD)!.get(item.uniqueId!)!,
											{ns: "message"}
										) : ""
								)}
							</>
						}}/>
						<Column field="roles" header={
							<>
								<Translate {...this.props} data="user.role.label" namespace="dialog"/>
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={User.getRolesDescription()}/>
							</>
						} body={(item: User & { index: number }) => {
							return <Dropdown disabled={admins <= 1 && item.roles!.indexOf("admin") > -1}
											 value={FunctionHelper.applyValue(item.roles, User.getRolesDefault())}
											 options={[
												 {
													 label: Translate.value(
														 "user.role.user", {ns: "dialog"}
													 ),
													 icon: "icon icon-user",
													 value: "user"
												 },
												 {
													 label: Translate.value(
														 "user.role.admin", {ns: "dialog"}
													 ),
													 icon: "icon icon-user admin",
													 value: "admin,user"
												 }
											 ]}
											 onChange={(e) => this.onChange(
												 UserDialogFields.ROLE, {
													 uniqueId: item.uniqueId, value: e.value
												 }
											 )}
											 appendTo={document.getElementById("root")}/>
						}}/>
						<Column body={(item: User & { index: number }) => {
							return <Button icon="icon icon-close"
										   tooltip={Translate.value("user.delete", {ns: "dialog"})}
										   disabled={admins <= 1 && item.roles!.indexOf("admin") > -1}
										   onClick={() => this.onChange(
											   UserDialogFields.DELETE, {
												   uniqueId: item.uniqueId
											   }
										   )}/>
						}}/>
					</DataTable>
				</div>
				<div className="p-col-12">
					<div className="p-col-12">
						<Button className="dialog-button" icon="icon icon-add"
								label={Translate.value("user.add", {ns: "dialog"})}
								onClick={() => this.onChange(UserDialogFields.ADD, {})}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["user-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.user.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.admin.user.info"} namespace={"help"} markdown={true}/>
						<Translate {...this.props} data={"dialog.admin.user.danger"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-user multiple"}/>
					<Translate {...this.props} data="user.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: UserDialogFields, value: { value?: any, uniqueId?: string }): void {
		let parameter: Users | undefined = this.state.parameter as Users;
		let errors: Map<UserDialogFields, Map<string, string>> = this.state.userFieldErrors;

		let userConfig: User | undefined;
		let userIndex: number = 0;

		if (FunctionHelper.exists(value.uniqueId)) {
			for (let [index, user] of FunctionHelper.applyValue(parameter.user, Users.getUserDefault()).entries()) {
				if (user.uniqueId === value.uniqueId) {
					userConfig = user;
					userIndex = index;
					break;
				}
			}
		}

		switch (field) {
			case UserDialogFields.USERNAME:
				if (!FunctionHelper.exists(value.value) || FunctionHelper.isEmpty(value.value)) {
					if (!errors.has(field)) {
						errors.set(field, new Map<string, string>());
					}

					errors.get(field)!.set(value.uniqueId!, "errors.fields.set");
				} else {
					errors.get(field)?.delete(value.uniqueId!);

					if (errors.get(field)?.size === 0) {
						errors.delete(field);
					}
				}

				userConfig!.username = value.value;
				break;
			case UserDialogFields.PASSWORD:
				errors.get(field)?.delete(value.uniqueId!);

				if (errors.get(field)?.size === 0) {
					errors.delete(field);
				}

				userConfig!.password = value.value;
				break;
			case UserDialogFields.ROLE:
				userConfig!.roles = value.value;
				break;
			case UserDialogFields.DELETE:
				errors.get(UserDialogFields.USERNAME)?.delete(value.uniqueId!);

				if (errors.get(UserDialogFields.USERNAME)?.size === 0) {
					errors.delete(UserDialogFields.USERNAME);
				}

				errors.get(UserDialogFields.PASSWORD)?.delete(value.uniqueId!);

				if (errors.get(UserDialogFields.PASSWORD)?.size === 0) {
					errors.delete(UserDialogFields.PASSWORD);
				}

				parameter.user!.splice(userIndex, 1);
				break;
			case UserDialogFields.ADD:
				let uniqueId = DomHelper.generateUUID();

				parameter.user!.push(User.fromJson({
					username: "",
					password: "",
					roles: "user",
					uniqueId: uniqueId
				} as UserInterface));

				if (!errors.has(UserDialogFields.USERNAME)) {
					errors.set(UserDialogFields.USERNAME, new Map<string, string>());
				}

				errors.get(UserDialogFields.USERNAME)!.set(uniqueId, "errors.fields.set");

				if (!errors.has(UserDialogFields.PASSWORD)) {
					errors.set(UserDialogFields.PASSWORD, new Map<string, string>());
				}

				errors.get(UserDialogFields.PASSWORD)!.set(uniqueId, "errors.fields.set");
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected renderFooter(): ReactNode {
		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.reset")} icon={"icon icon-rotate-270 small"}
							disabled={!this.state.hasChanges || this.state.submitted}
							className="reset-button" onClick={() => this.reset()}/>
					<Button label={Translate.value("buttons.save")} className="primary-button"
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
							disabled={!this.isValid()} onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		let userErrors: Map<UserDialogFields, Map<string, string>> = this.state.userFieldErrors;
		return super.isValid() && this.state.hasChanges && userErrors.size === 0;
	}

	protected async reset(): Promise<void> {
		this.state.userFieldErrors.clear();

		await super.reset();
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let administration: Administration = this.props.context.session.getAdministration();
		let userConfiguration: Users = this.state.parameter!.clone();

		try {
			await administration.updateConfiguration(ConfigurationType.User, userConfiguration);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.user", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
		this.hide();
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.USER;
	}
}

const UserDialog = React.memo(UserDialogDefinition,
	(
		prevProps: Readonly<AdminDialogProperties<Users>>,
		nextProps: Readonly<AdminDialogProperties<Users>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default UserDialog;